import { useState } from "react";
import { CrossPromo } from "../components/CrossPromo.jsx";
import { NerdStackFooter } from "../components/NerdStackFooter.jsx";
import { HomeButton } from "../components/HomeButton.jsx";
import { formatNum } from "../lib/formatNum.js";

const rgba = (hex, a) => {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${a})`;
};

const ORANGE = "#F97316";
const AMBER = "#F59E0B";
const ORANGE_LIGHT = "#FDBA74";

function parseNumbers(text) {
  if (!text.trim()) return [];
  return text.split(/[,\s\n]+/).map(s => parseFloat(s.trim())).filter(n => !isNaN(n));
}

function calcStats(nums) {
  if (nums.length === 0) return null;
  const sorted = [...nums].sort((a, b) => a - b);
  const n = nums.length;
  const sum = nums.reduce((a, b) => a + b, 0);
  const mean = sum / n;
  const median = n % 2 === 0 ? (sorted[n/2 - 1] + sorted[n/2]) / 2 : sorted[Math.floor(n/2)];

  // Mode
  const freq = {};
  nums.forEach(v => { freq[v] = (freq[v] || 0) + 1; });
  const maxFreq = Math.max(...Object.values(freq));
  const modes = Object.entries(freq).filter(([, f]) => f === maxFreq && f > 1).map(([v]) => parseFloat(v));

  const range = sorted[n - 1] - sorted[0];

  // Variance & Std Dev (population)
  const variance = nums.reduce((acc, v) => acc + (v - mean) ** 2, 0) / n;
  const stdDev = Math.sqrt(variance);

  // Sample variance & std dev
  const sampleVariance = n > 1 ? nums.reduce((acc, v) => acc + (v - mean) ** 2, 0) / (n - 1) : 0;
  const sampleStdDev = Math.sqrt(sampleVariance);

  // Quartiles
  const q1Idx = (n - 1) * 0.25;
  const q3Idx = (n - 1) * 0.75;
  const interpolate = (idx) => {
    const lower = Math.floor(idx);
    const upper = Math.ceil(idx);
    if (lower === upper) return sorted[lower];
    return sorted[lower] + (sorted[upper] - sorted[lower]) * (idx - lower);
  };
  const q1 = interpolate(q1Idx);
  const q3 = interpolate(q3Idx);
  const iqr = q3 - q1;

  // Geometric mean (only if all positive)
  const allPositive = nums.every(v => v > 0);
  const geoMean = allPositive ? Math.exp(nums.reduce((acc, v) => acc + Math.log(v), 0) / n) : null;

  // Harmonic mean (only if all positive)
  const harMean = allPositive ? n / nums.reduce((acc, v) => acc + 1/v, 0) : null;

  return { sorted, n, sum, mean, median, modes, range, variance, stdDev, sampleVariance, sampleStdDev, q1, q3, iqr, geoMean, harMean, min: sorted[0], max: sorted[n-1] };
}

function calcWeightedMean(values, weights) {
  if (values.length === 0 || values.length !== weights.length) return null;
  let totalWeight = 0, weightedSum = 0;
  for (let i = 0; i < values.length; i++) {
    if (isNaN(values[i]) || isNaN(weights[i])) continue;
    weightedSum += values[i] * weights[i];
    totalWeight += weights[i];
  }
  return totalWeight > 0 ? weightedSum / totalWeight : null;
}

function fmt(n) {
  if (n === null || n === undefined) return "N/A";
  return formatNum(n, 8);
}

function StatRow({ label, value, desc, color = ORANGE_LIGHT }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
      <div>
        <div style={{ fontSize: 14, color: "#D4D4D8" }}>{label}</div>
        {desc && <div style={{ fontSize: 11, color: "#4B5563", marginTop: 1 }}>{desc}</div>}
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );
}

function StatCard({ label, value, color = ORANGE_LIGHT }) {
  return (
    <div style={{
      padding: "16px 12px", borderRadius: 12, textAlign: "center",
      background: `linear-gradient(135deg, ${rgba(color, 0.08)}, ${rgba(color, 0.03)})`,
      border: `1px solid ${rgba(color, 0.15)}`,
    }}>
      <div style={{ fontSize: 22, fontWeight: 800, color, letterSpacing: "-0.5px" }}>{value}</div>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 4 }}>{label}</div>
    </div>
  );
}

export default function AverageCalcApp() {
  const [input, setInput] = useState("");
  const [tab, setTab] = useState("basic");
  const [wRows, setWRows] = useState([{ value: "", weight: "" }, { value: "", weight: "" }, { value: "", weight: "" }]);

  const nums = parseNumbers(input);
  const stats = calcStats(nums);

  const addWRow = () => setWRows([...wRows, { value: "", weight: "" }]);
  const updateW = (i, field, val) => { const u = [...wRows]; u[i] = { ...u[i], [field]: val }; setWRows(u); };
  const removeW = (i) => { if (wRows.length > 2) setWRows(wRows.filter((_, idx) => idx !== i)); };

  const wValues = wRows.map(r => parseFloat(r.value)).filter(n => !isNaN(n));
  const wWeights = wRows.filter(r => !isNaN(parseFloat(r.value))).map(r => parseFloat(r.weight) || 1);
  const weightedResult = calcWeightedMean(wValues, wWeights);

  const tabs = [
    { id: "basic", label: "Basic Stats", icon: "📊" },
    { id: "advanced", label: "Advanced", icon: "📐" },
    { id: "weighted", label: "Weighted Mean", icon: "⚖️" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", fontFamily: "'DM Sans', system-ui, sans-serif", color: "#E2E8F0" }}>
      <div style={{ position: "fixed", top: "-25%", left: "-10%", width: "50vw", height: "50vw", background: `radial-gradient(circle, ${rgba(ORANGE, 0.04)} 0%, transparent 65%)`, pointerEvents: "none" }} />

      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", paddingTop: "calc(16px + env(safe-area-inset-top))", paddingLeft: "calc(24px + env(safe-area-inset-left))", paddingRight: "calc(24px + env(safe-area-inset-right))", borderBottom: `1px solid ${rgba(ORANGE, 0.06)}`, position: "relative", zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <HomeButton accentColor={ORANGE} />
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg, ${ORANGE}, ${AMBER})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: "#fff" }}>x̄</div>
          <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", background: `linear-gradient(135deg, ${ORANGE_LIGHT}, #FDE68A)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>AverageCalc</span>
          <span style={{ fontSize: 11, color: "#374151", marginLeft: 4 }}>by NerdStack</span>
        </div>
        <div style={{ fontSize: 12, color: "#374151" }}>100% free</div>
      </header>

      <main style={{ maxWidth: 680, margin: "0 auto", padding: "32px 20px 80px", position: "relative", zIndex: 10 }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <h1 style={{ fontSize: 38, fontWeight: 800, margin: "0 0 8px", lineHeight: 1.1, letterSpacing: "-1px", background: `linear-gradient(135deg, #FEF3C7, ${ORANGE_LIGHT} 40%, #FDE68A 70%, #FEF9C3)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            Average and Statistics Calculator
          </h1>
          <p style={{ fontSize: 15, color: "#6B7280", margin: 0 }}>Mean, median, mode, standard deviation, quartiles, and more.</p>
        </div>

        {/* Tabs */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 24 }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: "12px 10px", borderRadius: 14, cursor: "pointer", fontFamily: "inherit",
              background: tab === t.id ? rgba(ORANGE, 0.1) : "rgba(255,255,255,0.02)",
              border: tab === t.id ? `1.5px solid ${rgba(ORANGE, 0.3)}` : "1.5px solid rgba(255,255,255,0.05)",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              transition: "all 0.2s ease",
            }}>
              <span style={{ fontSize: 16 }}>{t.icon}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: tab === t.id ? ORANGE_LIGHT : "#6B7280" }}>{t.label}</span>
            </button>
          ))}
        </div>

        <div style={{ padding: "24px 22px", borderRadius: 20, background: "rgba(10,11,20,0.6)", border: `1px solid ${rgba(ORANGE, 0.08)}`, boxShadow: "0 4px 30px rgba(0,0,0,0.2)" }}>

          {/* Basic and Advanced share the same input */}
          {(tab === "basic" || tab === "advanced") && (
            <div>
              <p style={{ fontSize: 14, color: "#6B7280", marginTop: 0, marginBottom: 12 }}>
                Enter numbers separated by commas, spaces, or new lines.
              </p>
              <textarea value={input} onChange={e => setInput(e.target.value)}
                placeholder="Example: 85, 92, 78, 90, 88, 76, 95, 82"
                style={{
                  width: "100%", minHeight: 80, padding: 16, borderRadius: 12,
                  border: `1px solid ${rgba(ORANGE, 0.12)}`, background: "rgba(0,0,0,0.3)",
                  color: "#E2E8F0", fontSize: 15, fontFamily: "'JetBrains Mono', monospace",
                  lineHeight: 1.6, outline: "none", boxSizing: "border-box", resize: "vertical",
                }} />
              {nums.length > 0 && (
                <div style={{ fontSize: 12, color: "#4B5563", marginTop: 6 }}>
                  {nums.length} number{nums.length !== 1 ? "s" : ""} detected: [{stats.sorted.join(", ")}]
                </div>
              )}
            </div>
          )}

          {/* Basic results */}
          {tab === "basic" && stats && (
            <div style={{ marginTop: 20 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 16 }}>
                <StatCard label="Mean" value={fmt(stats.mean)} />
                <StatCard label="Median" value={fmt(stats.median)} />
                <StatCard label="Mode" value={stats.modes.length > 0 ? stats.modes.map(fmt).join(", ") : "None"} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
                <StatCard label="Sum" value={fmt(stats.sum)} color="#6B7280" />
                <StatCard label="Count" value={stats.n.toString()} color="#6B7280" />
                <StatCard label="Min" value={fmt(stats.min)} color="#6B7280" />
                <StatCard label="Max" value={fmt(stats.max)} color="#6B7280" />
              </div>
            </div>
          )}

          {/* Advanced results */}
          {tab === "advanced" && stats && (
            <div style={{ marginTop: 20 }}>
              <StatRow label="Range" value={fmt(stats.range)} desc="Max - Min" />
              <StatRow label="Std Deviation (Population)" value={fmt(stats.stdDev)} desc="σ — spread of all data" />
              <StatRow label="Std Deviation (Sample)" value={fmt(stats.sampleStdDev)} desc="s — spread estimate from sample" />
              <StatRow label="Variance (Population)" value={fmt(stats.variance)} desc="σ²" />
              <StatRow label="Variance (Sample)" value={fmt(stats.sampleVariance)} desc="s²" />
              <StatRow label="Q1 (25th Percentile)" value={fmt(stats.q1)} desc="Lower quartile" />
              <StatRow label="Median (50th)" value={fmt(stats.median)} desc="Middle value" />
              <StatRow label="Q3 (75th Percentile)" value={fmt(stats.q3)} desc="Upper quartile" />
              <StatRow label="IQR" value={fmt(stats.iqr)} desc="Interquartile range (Q3 - Q1)" />
              <StatRow label="Geometric Mean" value={stats.geoMean !== null ? fmt(stats.geoMean) : "N/A"} desc={stats.geoMean === null ? "Requires all positive numbers" : "Used for growth rates"} color={stats.geoMean !== null ? ORANGE_LIGHT : "#4B5563"} />
              <StatRow label="Harmonic Mean" value={stats.harMean !== null ? fmt(stats.harMean) : "N/A"} desc={stats.harMean === null ? "Requires all positive numbers" : "Used for averaging rates"} color={stats.harMean !== null ? ORANGE_LIGHT : "#4B5563"} />
            </div>
          )}

          {/* Weighted mean */}
          {tab === "weighted" && (
            <div>
              <p style={{ fontSize: 14, color: "#6B7280", marginTop: 0, marginBottom: 16 }}>
                Enter values and their weights. Great for calculating weighted GPAs, grade averages, or any scenario where some values count more than others.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                {wRows.map((row, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "end" }}>
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
                      {i === 0 && <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Value</label>}
                      <input type="number" value={row.value} onChange={e => updateW(i, "value", e.target.value)} placeholder="90"
                        style={{
                          width: "100%", padding: "11px 14px", borderRadius: 10,
                          border: `1px solid ${rgba(ORANGE, 0.12)}`, background: "rgba(0,0,0,0.3)",
                          color: "#E2E8F0", fontSize: 15, fontFamily: "'DM Sans', system-ui, sans-serif",
                          outline: "none", boxSizing: "border-box",
                        }} />
                    </div>
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
                      {i === 0 && <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Weight</label>}
                      <input type="number" value={row.weight} onChange={e => updateW(i, "weight", e.target.value)} placeholder="1"
                        style={{
                          width: "100%", padding: "11px 14px", borderRadius: 10,
                          border: `1px solid ${rgba(ORANGE, 0.12)}`, background: "rgba(0,0,0,0.3)",
                          color: "#E2E8F0", fontSize: 15, fontFamily: "'DM Sans', system-ui, sans-serif",
                          outline: "none", boxSizing: "border-box",
                        }} />
                    </div>
                    <button onClick={() => removeW(i)} style={{
                      width: 36, height: 40, borderRadius: 10, border: "1px solid rgba(248,113,113,0.15)",
                      background: "rgba(248,113,113,0.05)", color: "#F87171",
                      cursor: wRows.length <= 2 ? "not-allowed" : "pointer",
                      fontSize: 16, fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center",
                      opacity: wRows.length <= 2 ? 0.3 : 1, marginTop: i === 0 ? 19 : 0, flexShrink: 0,
                    }}>x</button>
                  </div>
                ))}
              </div>
              <button onClick={addWRow} style={{
                width: "100%", padding: "12px", borderRadius: 12, cursor: "pointer",
                background: rgba(ORANGE, 0.08), border: `1px solid ${rgba(ORANGE, 0.15)}`,
                color: ORANGE_LIGHT, fontSize: 14, fontWeight: 600, fontFamily: "inherit", marginBottom: 20,
              }}>+ Add Row</button>

              {weightedResult !== null && (
                <StatCard label="Weighted Mean" value={fmt(weightedResult)} color={ORANGE_LIGHT} />
              )}

              <div style={{ marginTop: 16, padding: "14px 16px", borderRadius: 12, background: rgba(ORANGE, 0.03), border: `1px solid ${rgba(ORANGE, 0.06)}`, fontSize: 12, color: "#4B5563", lineHeight: 1.6 }}>
                <strong style={{ color: "#6B7280" }}>Example uses:</strong> calculating your course grade when assignments have different weights (homework 20%, midterm 30%, final 50%), or computing a weighted GPA across courses with different credit hours.
              </div>
            </div>
          )}

          {/* Empty state */}
          {(tab === "basic" || tab === "advanced") && !stats && (
            <div style={{ textAlign: "center", padding: "20px 0", color: "#374151", fontSize: 14 }}>
              Enter some numbers above to see results.
            </div>
          )}
        </div>

        {/* Info cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 32 }}>
          {[
            { icon: "📊", title: "Basic Stats", text: "Mean, median, mode, sum, count, min, max — all the essentials." },
            { icon: "📐", title: "Advanced", text: "Std deviation, variance, quartiles, IQR, geometric and harmonic means." },
            { icon: "⚖️", title: "Weighted", text: "Weighted average for grades, GPAs, or any data with varying importance." },
          ].map((item, i) => (
            <div key={i} style={{ padding: "18px 14px", borderRadius: 14, background: rgba(ORANGE, 0.02), border: `1px solid ${rgba(ORANGE, 0.05)}` }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{item.icon}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: ORANGE_LIGHT, marginBottom: 4 }}>{item.title}</div>
              <div style={{ fontSize: 11, color: "#6B7280", lineHeight: 1.6 }}>{item.text}</div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 32, padding: "20px", borderRadius: 16, background: `linear-gradient(135deg, ${rgba(ORANGE, 0.06)}, ${rgba(AMBER, 0.06)})`, border: `1px solid ${rgba(ORANGE, 0.12)}`, textAlign: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: ORANGE_LIGHT, marginBottom: 6 }}>More NerdStack tools</div>
          {/* Cross-promo (rotates per page load) */}
          <CrossPromo currentSlug="average" accent={ORANGE} accent2={AMBER} accentLight={ORANGE_LIGHT} />
        </div>
      </main>

      <footer style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#1F2937", borderTop: `1px solid ${rgba(ORANGE, 0.04)}` }}>
        <NerdStackFooter variant="tool" family="tools" />
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        input[type="number"]::-webkit-inner-spin-button { -webkit-appearance: none; }
        input[type="number"] { -moz-appearance: textfield; }
        input::placeholder, textarea::placeholder { color: #374151; }
        * { box-sizing: border-box; }
        body { margin: 0; }
      `}</style>
    </div>
  );
}
